import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { hasAnyPermission, hasPermission } from '@barahpay/shared';
import type { LoginResponse, PermissionName, UserProfile } from '@barahpay/shared';
import { api } from '../api/endpoints';
import { onUnauthorized, tokenStore } from '../api/client';

interface AuthState {
  user: UserProfile | null;
  /** True while the initial session restore is running, so routes do not flash the sign-in page. */
  restoring: boolean;
  signIn: (email: string, password: string, mfaCode?: string) => Promise<void>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
  can: (permission: PermissionName) => boolean;
  canAny: (permissions: PermissionName[]) => boolean;
  /** Set when the API says MFA is required, so the sign-in form can ask for the code. */
  mfaRequired: boolean;
  clearMfaRequirement: () => void;
}

const AuthContext = createContext<AuthState | null>(null);

/** The API error code returned when a correct password still needs a TOTP code (spec section 138). */
const MFA_REQUIRED = 'MFA_REQUIRED';

/**
 * The session restore is a page-lifetime singleton, held at module scope rather than in a ref.
 *
 * Refresh tokens are single-use and replaying one revokes every session for that user, which is the
 * correct server behaviour. React StrictMode deliberately mounts effects twice in development, so a
 * per-component guard would still fire two refreshes with the same token: the first would succeed and
 * the second would be treated as a replay, signing the user out. Sharing one promise means the second
 * mount awaits the first result instead of spending the token again.
 */
let restorePromise: Promise<LoginResponse | null> | null = null;

function restoreSessionOnce(): Promise<LoginResponse | null> {
  restorePromise ??= (async () => {
    const refreshToken = tokenStore.getRefreshToken();

    if (!refreshToken) {
      return null;
    }

    try {
      const session = await api.auth.refresh(refreshToken);
      tokenStore.setTokens(session.accessToken, session.refreshToken);
      return session;
    } catch {
      tokenStore.clear();
      return null;
    }
  })();

  return restorePromise;
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [restoring, setRestoring] = useState(true);
  const [mfaRequired, setMfaRequired] = useState(false);

  // Idle timeout is driven by the value the API reports at sign-in, not a hard-coded constant.
  const idleTimeoutSeconds = useRef<number>(0);
  const idleTimer = useRef<number | null>(null);

  const clearSession = useCallback(() => {
    tokenStore.clear();
    // Keep the page-lifetime restore result coherent: a remount must not resurrect a dead session.
    restorePromise = Promise.resolve(null);
    setUser(null);
    if (idleTimer.current !== null) {
      window.clearTimeout(idleTimer.current);
      idleTimer.current = null;
    }
  }, []);

  const signOut = useCallback(async () => {
    const refreshToken = tokenStore.getRefreshToken();

    if (refreshToken) {
      // A failed revoke must not trap the user in a signed-in shell, so the local session is
      // cleared regardless.
      try {
        await api.auth.logout(refreshToken);
      } catch {
        /* ignored on purpose */
      }
    }

    clearSession();
  }, [clearSession]);

  const armIdleTimer = useCallback(() => {
    if (idleTimer.current !== null) {
      window.clearTimeout(idleTimer.current);
    }

    if (idleTimeoutSeconds.current <= 0) {
      return;
    }

    idleTimer.current = window.setTimeout(() => {
      void signOut();
    }, idleTimeoutSeconds.current * 1000);
  }, [signOut]);

  // A 401 that could not be refreshed ends the session immediately.
  useEffect(() => {
    onUnauthorized(() => clearSession());
  }, [clearSession]);

  // Any real interaction resets the idle countdown (spec section 138).
  useEffect(() => {
    if (!user) {
      return;
    }

    const reset = () => armIdleTimer();
    const events: (keyof WindowEventMap)[] = ['click', 'keydown', 'mousemove', 'scroll'];
    events.forEach((event) => window.addEventListener(event, reset, { passive: true }));
    armIdleTimer();

    return () => {
      events.forEach((event) => window.removeEventListener(event, reset));
      if (idleTimer.current !== null) {
        window.clearTimeout(idleTimer.current);
      }
    };
  }, [user, armIdleTimer]);

  // A page reload keeps the session alive if the refresh token in sessionStorage is still valid.
  useEffect(() => {
    let cancelled = false;

    void restoreSessionOnce().then((session) => {
      if (cancelled) {
        return;
      }

      if (session) {
        idleTimeoutSeconds.current = session.idleTimeoutSeconds;
        setUser(session.user);
      }

      setRestoring(false);
    });

    return () => {
      cancelled = true;
    };
  }, []);

  const signIn = useCallback(async (email: string, password: string, mfaCode?: string) => {
    try {
      const session = await api.auth.login(email, password, mfaCode);
      tokenStore.setTokens(session.accessToken, session.refreshToken);
      restorePromise = Promise.resolve(session);
      idleTimeoutSeconds.current = session.idleTimeoutSeconds;
      setMfaRequired(false);
      setUser(session.user);
    } catch (error) {
      const code = (error as { errorCode?: string }).errorCode;

      if (code === MFA_REQUIRED) {
        setMfaRequired(true);
      }

      throw error;
    }
  }, []);

  const refreshProfile = useCallback(async () => {
    const profile = await api.auth.me();
    setUser(profile);
  }, []);

  const value = useMemo<AuthState>(
    () => ({
      user,
      restoring,
      signIn,
      signOut,
      refreshProfile,
      can: (permission) => hasPermission(user, permission),
      canAny: (permissions) => hasAnyPermission(user, permissions),
      mfaRequired,
      clearMfaRequirement: () => setMfaRequired(false),
    }),
    [user, restoring, signIn, signOut, refreshProfile, mfaRequired],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used inside an AuthProvider.');
  }

  return context;
}
